import {
  Auth0Context,
  type Auth0ContextInterface,
  initialContext,
  type LogoutOptions,
} from '@auth0/auth0-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { TrialClosed, type TrialClosedReason } from '../components/trial-closed';
import { registerTrialSessionHandler, TrialEndedError } from '../helpers/api-errors';
import { fixedTokenAccessor } from './fixed-token-accessor';
import { clearTrialSession, type TrialSession, trialBearerToken } from './trial-session';

/**
 * Builds the Auth0 context a trial link session runs the app under: always
 * authenticated, its token the trial bearer token. On the DemoAuthProvider
 * pattern, with a per-store token instead of the shared sentinel.
 */
export function trialAuthContext(session: TrialSession): Auth0ContextInterface {
  // Signing out ends the trial session and goes where Auth0's logout would:
  // its returnTo (Exit demo passes the marketing site), else the app, which
  // reloads into an ordinary Auth0 session. Signing in means the latter:
  // nothing about a trial can sign in again.
  const endSession = (options?: LogoutOptions) => {
    clearTrialSession();
    window.location.assign(options?.logoutParams?.returnTo ?? '/');

    return Promise.resolve();
  };

  return {
    ...initialContext,
    isAuthenticated: true,
    isLoading: false,
    user: { sub: `trial|${session.storeId}`, name: 'Trial' },
    getAccessTokenSilently: fixedTokenAccessor(trialBearerToken(session)),
    loginWithRedirect: () => endSession(),
    logout: endSession,
  };
}

/**
 * Keeps a trial's link out of search engines and out of the Referer of
 * anything the page loads or links to. These tags are the only copy: CloudFront
 * serves /t/* through its 404 → index.html error response, which never applies
 * a cache behavior's response headers policy, so header-based versions would
 * do nothing.
 */
function useTrialPageMeta() {
  useEffect(() => {
    const tags = [
      Object.assign(document.createElement('meta'), { name: 'referrer', content: 'no-referrer' }),
      Object.assign(document.createElement('meta'), { name: 'robots', content: 'noindex' }),
    ];
    document.head.prepend(...tags);

    return () => {
      for (const tag of tags) {
        tag.remove();
      }
    };
  }, []);
}

export function TrialAuthProvider({
  session,
  children,
}: {
  session: TrialSession;
  children: ReactNode;
}) {
  const context = useMemo(() => trialAuthContext(session), [session]);
  const [closed, setClosed] = useState<TrialClosedReason | null>(null);

  useTrialPageMeta();

  useEffect(() => {
    registerTrialSessionHandler((error) => {
      // The first reason sticks: requests in flight when it lands fail too.
      setClosed((current) => current ?? (error instanceof TrialEndedError ? 'ended' : 'invalid'));
    });

    return () => registerTrialSessionHandler(null);
  }, []);

  if (closed) {
    return <TrialClosed reason={closed} onSignIn={() => void context.logout()} />;
  }

  return <Auth0Context.Provider value={context}>{children}</Auth0Context.Provider>;
}
